/* Scroll reveal.

   Two sets of targets share one IntersectionObserver:

   REPLAY -- the decorative homepage sections this started with. They fade
   and rise when they enter and reset when they leave below, so they play
   again on every downward pass (unchanged behaviour).

   ONCE -- everything else: section heads, cards, steps, stats, media. They
   reveal the first time they enter and are then left alone; product cards
   and content that replays on every scroll gets tiring fast.

   Rules that keep this safe for shopping:
   - Nothing that is on screen when the script runs is hidden. Those
     elements stay exactly as painted, so products, prices and the buy box
     are never delayed and nothing blinks out and back in.
   - Elements that enter together cascade in reading order (by batch, not by
     DOM index), so the 7th card of a grid doesn't wait half a second.
   - Never touched: anything inside the theme's own <motion-element> (it
     animates itself), marquees, the arc carousel's slides (inline transforms
     every frame), the header, drawers and modals, and the product buy box.
   - prefers-reduced-motion: nothing is hidden or moved. */
(function () {
  'use strict';

  var REPLAY = [
    '.ts-pdp',
    '.ts-cats__item',
    '.ts-story__media',
    '.ts-story__body',
    '.ts-story__point',
    '.ts-arc__viewport',
    '.ts-arc__header',
    '.ts-arc__controls',
    '.ts-arc__cta'
  ].join(',');

  // [selector, variant]. Variant: '' = rise, 'fade', 'zoom', 'left', 'right'.
  var ONCE = [
    // Section heads
    ['.ts-stats__heading, .ts-ev__eyebrow, .ts-ev__heading, .ts-ev__intro, .ts-tl__head, .ts-cm__head, .ts-steps__head, .ts-rel__head, .ts-coll-more__head, .ts-bloggrid__head', ''],
    ['.ts-bloggrid__toolbar, .ts-steps__footnote, .ts-coll-desc', 'fade'],
    // Cards and list items (cascade)
    ['.ts-stats__item, .ts-ev__card, .ts-tl__item, .ts-cm__item, .ts-steps__item, .ts-trust__item, .ts-sci__stat, .ts-acct__benefit', ''],
    ['.ts-bcard, .ts-rel__item, .ts-coll-more .f-column, #ProductsList > .f-column', ''],
    // Split layouts: media and text come from their own side
    ['.ts-sb__media, .ts-sci__media', 'zoom'],
    ['.ts-sb__text, .ts-sci__body', '']
  ];

  var SKIP = 'motion-element, .ts-marq, .ts-cmarq, .ts-arc__slide, header, .header, .drawer, .modal, details-modal, quick-view, .product__info-wrapper, .product-form';
  var STEP = 70;      // ms between cascading siblings
  var MAX_STEPS = 6;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var io = null;
  var once = new WeakSet();

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight * 0.95 && r.width > 0;
  }

  function onIntersect(entries) {
    var entering = [];
    entries.forEach(function (e) {
      var el = e.target;
      if (e.isIntersecting) {
        if (once.has(el)) entering.push(el);
        else el.classList.add('ts-rv-in');
      } else if (!once.has(el) && e.boundingClientRect.top > 0) {
        // Replay set: reset only once fully below, so a partly visible
        // element doesn't flicker mid-scroll.
        el.classList.remove('ts-rv-in');
      }
    });
    if (!entering.length) return;
    // Cascade what arrived together in reading order.
    entering.sort(function (a, b) {
      var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
      return (ra.top - rb.top) || (ra.left - rb.left);
    });
    entering.forEach(function (el, i) {
      el.style.setProperty('--ts-rv-delay', Math.min(i, MAX_STEPS) * STEP + 'ms');
      el.classList.add('ts-rv-in');
      io.unobserve(el);
    });
  }

  function staggerByParent(nodes) {
    var byParent = new Map();
    nodes.forEach(function (n) {
      var p = n.parentNode;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(n);
    });
    byParent.forEach(function (group) {
      group.forEach(function (n, i) {
        n.style.setProperty('--ts-rv-delay', Math.min(i, MAX_STEPS) * 90 + 'ms');
      });
    });
  }

  function ensureObserver() {
    if (io) return io;
    io = new IntersectionObserver(onIntersect, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    return io;
  }

  function armReplay(root) {
    var nodes = Array.prototype.slice.call(root.querySelectorAll(REPLAY))
      .filter(function (n) { return !n.classList.contains('ts-rv'); });
    if (!nodes.length) return;
    staggerByParent(nodes);
    nodes.forEach(function (n) {
      n.classList.add('ts-rv');
      ensureObserver().observe(n);
    });
  }

  function armOnce(root) {
    ONCE.forEach(function (pair) {
      root.querySelectorAll(pair[0]).forEach(function (el) {
        if (el.classList.contains('ts-rv') || el.closest(SKIP)) return;
        if (onScreen(el)) return;                // already painted: leave it
        if (pair[1]) el.setAttribute('data-rv', pair[1]);
        el.classList.add('ts-rv');
        once.add(el);
        ensureObserver().observe(el);
      });
    });
  }

  function init(root) {
    root = root && root.querySelectorAll ? root : document;
    if (reduced.matches) {
      root.querySelectorAll(REPLAY).forEach(function (n) { n.classList.add('ts-rv-in'); });
      return;
    }
    if (!('IntersectionObserver' in window)) return;   // content just shows
    armReplay(root);
    armOnce(root);
  }

  // Content that arrives after load (product recommendations) gets the same
  // treatment when it lands.
  function watchLate() {
    if (reduced.matches || !('MutationObserver' in window)) return;
    document.querySelectorAll('product-recommendations').forEach(function (host) {
      new MutationObserver(function () { init(host); }).observe(host, { childList: true, subtree: true });
    });
  }

  function start() { init(document); watchLate(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else { start(); }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
